/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaRunNext.ts
 * FILE_ID: JOBHUB-QA-RUN-NEXT-001
 * PURPOSE: Next checklist and next area for the Run bar. No React.
 */
import type { QaArea, QaFloor } from '../../qa/qaApi'
import { groupNameOf, levelsOf } from './qaTabSelectors'

export type RunArea = { id: string; name: string; group?: string }
export type RunChecklist = { id: string; name: string }
export type RunCatalog = Record<string, RunChecklist[] | undefined>

export type RunSuggestion = {
  areaId: string
  checklistId: string | null
  pending: boolean
}

type ScopeArea = { id: string; area_name: string; floor?: string | null }

/** Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaTabSelectors.ts (levelsOf, groupNameOf). */
export function orderedScopeAreas(floors: QaFloor[], areas: readonly ScopeArea[]): RunArea[] {
  return levelsOf(floors, areas as QaArea[]).flatMap(({ areas: levelAreas }) =>
    levelAreas.map((area) => ({
      id: area.id,
      name: area.area_name,
      group: groupNameOf(area, floors) || undefined,
    })),
  )
}

function listsOf(
  areaId: string,
  catalog: RunCatalog,
  viewedAreaId: string,
  currentChecklists: readonly RunChecklist[],
): { list: RunChecklist[] | undefined; pending: boolean } {
  if (areaId === viewedAreaId && !(areaId in catalog)) {
    return { list: [...currentChecklists], pending: false }
  }
  if (!(areaId in catalog) || catalog[areaId] === undefined) {
    return { list: undefined, pending: true }
  }
  return { list: catalog[areaId], pending: false }
}

export function suggestNextRun({
  areaIds,
  catalog,
  viewedAreaId,
  viewedChecklistId,
  currentChecklists,
}: {
  areaIds: readonly string[]
  catalog: RunCatalog
  viewedAreaId: string
  viewedChecklistId: string | null
  currentChecklists: readonly RunChecklist[]
}): RunSuggestion {
  const viewed = listsOf(viewedAreaId, catalog, viewedAreaId, currentChecklists)
  const ids = (viewed.list ?? currentChecklists).map((row) => row.id)
  const index = viewedChecklistId ? ids.indexOf(viewedChecklistId) : -1
  if (index >= 0 && index < ids.length - 1) {
    return { areaId: viewedAreaId, checklistId: ids[index + 1], pending: false }
  }
  const areaIndex = areaIds.indexOf(viewedAreaId)
  const nextAreaId = areaIds[(areaIndex < 0 ? 0 : areaIndex + 1) % Math.max(areaIds.length, 1)] ?? viewedAreaId
  const next = listsOf(nextAreaId, catalog, viewedAreaId, currentChecklists)
  if (next.pending) return { areaId: nextAreaId, checklistId: null, pending: true }
  const first = next.list?.[0]?.id ?? null
  return { areaId: nextAreaId, checklistId: first, pending: false }
}

export function stepSelection(ids: readonly string[], currentId: string, delta: -1 | 1): string | null {
  if (ids.length <= 1) return null
  const index = ids.indexOf(currentId)
  if (index < 0) return null
  const next = index + delta
  if (next < 0 || next >= ids.length) return null
  return ids[next]
}
